import { PillTabs } from 'components/FbUI/PillTabs'
import { isTimeSeries } from '../pipeline-ops'
import { QueryResult, ViewConfig } from '../view-config'
import { ReportChartType } from './ReportChart'

/**
 * How a single-component report can be *viewed*. A report is one element; the
 * view switcher only changes its presentation (it never filters or reshapes the
 * data — that's editing). Which views are offered is gated by the data shape,
 * mirroring the report-builder gallery:
 *   - table:   always
 *   - metric:  only for an aggregate (shows the rollup total as one number)
 *   - charts:  gated by the breakdown's shape, so an encoding never implies a
 *              structure the data lacks (mirrors the pipeline builder):
 *       · categorical breakdown → bar / horizontal-bar / ranked
 *       · time bucket (month/quarter) → bar / horizontal-bar / line
 *              Line is time-series-only (a line over categories implies a trend
 *              that isn't there); ranked is categorical-only (it loses the time
 *              ordering); stacked/grouped when there's a series. No pie/donut —
 *              bar/ranked encode part-of-whole on a readable common scale.
 *              Two+ measures with no dual-axis combo are table-only — different
 *              units (count vs $ vs days) can't share one chart scale.
 */
export type ReportView = 'table' | 'metric' | ReportChartType

/** The views valid for a report's current shape, in display order. */
export function availableViews(
  config: ViewConfig,
  result: QueryResult
): { view: ReportView; label: string }[] {
  // A metric report (aggregate, no groupBy) resolves to one value — it's just
  // the number. No Table/chart views and so no switcher at all.
  if (result.kind === 'aggregate' && !config.groupBy) {
    return [{ view: 'metric', label: 'Metric' }]
  }

  const seriesCount =
    result.kind === 'aggregate' ? (result.seriesKeys?.length ?? 0) : 0
  const hasSeries = !!config.seriesBy && seriesCount > 0
  // Two+ measures can't share one chart: count vs $ vs days are different units
  // on incomparable scales, so a single encoding would mislead. The dual-axis
  // combo (chartMetric2) is the one designed two-measure chart; any other
  // multi-metric aggregate is table-only.
  const multiMeasure = (config.metrics?.length ?? 0) > 1 && !config.chartMetric2
  // With a split, the table becomes a cross-tab (group × split) — an Excel pivot.
  const views: { view: ReportView; label: string }[] = [
    { view: 'table', label: hasSeries ? 'Pivot' : 'Table' },
  ]
  if (result.kind === 'aggregate' && !multiMeasure) {
    if (hasSeries) {
      // A pivot (rows × columns): the cross-tab Pivot table, a stacked bar, or
      // the column split drawn as one line / one dot-series per column over the
      // row axis (lines connect, dots don't over-imply continuity).
      views.push({ view: 'stacked-bar', label: 'Stacked' })
      views.push({ view: 'grouped-bar', label: 'Grouped' })
      if (isTimeSeries(config)) {
        views.push({ view: 'line', label: 'Split (lines)' })
        views.push({ view: 'dot', label: 'Split (dots)' })
      }
    } else if (config.chartMetric2) {
      // Two measures (dual-axis combo) → only the encodings that show BOTH as
      // grouped double bars. Line/ranked would each show just one, so we hide
      // them rather than silently dropping the second measure.
      views.push({ view: 'bar', label: 'Bar' })
      views.push({ view: 'horizontal-bar', label: 'Horizontal' })
    } else {
      views.push({ view: 'bar', label: 'Bar' })
      views.push({ view: 'horizontal-bar', label: 'Horizontal' })
      if (isTimeSeries(config)) {
        // Ordered time axis → a line reads as a real trend; dots when a line
        // over-implies continuity between discrete counts (Tufte).
        views.push({ view: 'line', label: 'Line' })
        views.push({ view: 'dot', label: 'Dots' })
      } else {
        // Categorical breakdown → leaderboard (no line, no pie/donut).
        views.push({ view: 'ranked', label: 'Ranked' })
      }
    }
  }
  return views
}

/** The view a report opens in: metric, its saved chart, or a table. */
export function defaultView(config: ViewConfig): ReportView {
  if (config.kind === 'list') return 'table'
  // Metric report: aggregate with no group-by → one number.
  if (config.kind === 'aggregate' && !config.groupBy) return 'metric'
  if (config.chart === 'table-only') return 'table'
  // Line/dot only read on an ordered time axis — on a categorical breakdown open
  // on bar instead of a toggle availableViews no longer offers.
  if (
    (config.chart === 'line' || config.chart === 'dot') &&
    !isTimeSeries(config)
  )
    return config.seriesBy ? 'stacked-bar' : 'bar'
  return config.chart
}

/**
 * The view to actually show: a caller's explicit override when the current shape
 * still supports it, else the config's default — always clamped to an available
 * view so the switcher and body never disagree. Shared by every report preview
 * (flat editor, pipeline builder, pipeline detail) so they resolve identically.
 *
 * @param config - the report config
 * @param result - its computed result (drives which views are valid)
 * @param override - the user's last explicit "Show as" pick, or null for default
 * @returns a view guaranteed to be in `availableViews(config, result)`
 */
export function resolveView(
  config: ViewConfig,
  result: QueryResult,
  override: ReportView | null = null
): ReportView {
  const views = availableViews(config, result)
  const wanted = override ?? defaultView(config)
  return views.some((v) => v.view === wanted)
    ? wanted
    : (views[0]?.view ?? 'table')
}

/**
 * Segmented control for switching a report's view (Table / chart styles /
 * Metric). Renders nothing when only one view is available (e.g. a raw table
 * report), so callers can drop it in unconditionally.
 */
export function ViewSwitcher({
  config,
  result,
  view,
  onChange,
}: {
  config: ViewConfig
  result: QueryResult
  view: ReportView
  onChange: (view: ReportView) => void
}) {
  const views = availableViews(config, result)
  if (views.length <= 1) return null
  return (
    <PillTabs
      className="w-fit"
      idPrefix={`view-${config.id}`}
      aria-label="View"
      tabs={views.map((v) => ({ key: v.view, title: v.label }))}
      activeKey={view}
      onChange={(key) => onChange(key as ReportView)}
    />
  )
}
